import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import "./Screens.css";
import { updateOrderItems } from "../../services/screenService";
import { useWebSocket } from "../../hooks/useWebSocket";

const ScreensPage = () => {
    const { screenId } = useParams();
    const [orders, setOrders] = useState([]);
    const [currentScreenId, setCurrentScreenId] = useState(null);
    const [currentScreenName, setCurrentScreenName] = useState("");
    
    // History state
    const [showHistory, setShowHistory] = useState(false);
    const [historyOrders, setHistoryOrders] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Converts a "HH:MM:SS" string to milliseconds
    const parseTimerString = useCallback((timerString) => {
        const [hours, minutes, seconds] = timerString.split(":").map(Number);
        return (hours * 3600 + minutes * 60 + seconds) * 1000;
    }, []);

    // Formats milliseconds into an "M:SS" string
    const formatTime = useCallback((timeInMs) => {
        const minutes = Math.floor(timeInMs / (1000 * 60));
        const seconds = Math.floor((timeInMs % (1000 * 60)) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }, []);

    useEffect(() => {
        // Decrement remainingTime for all orders every second
        const interval = setInterval(() => {
            setOrders((prevOrders) =>
                prevOrders.map((order) => ({
                    ...order,
                    remainingTime: Math.max(0, order.remainingTime - 1000),
                }))
            );
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // WebSocket message handler
    const handleWebSocketMessage = useCallback((message) => {
        if (message.screen) {
            setCurrentScreenId(message.screen.id);
            setCurrentScreenName(message.screen.name);
        }

        if (message.orders) {
            const updatedOrders = message.orders.map((newOrder) => {
                const elapsedTime =
                    new Date().getTime() - new Date(newOrder.created_at).getTime();

                const remainingTime = Math.max(
                    0,
                    parseTimerString(newOrder.timer) - elapsedTime
                );

                // Sort the tech_cards for each product by name and sort items by name
                newOrder.items = newOrder.items
                    .map((item) => ({
                        ...item,
                        product: {
                            ...item.product,
                            tech_cards: item.product.tech_cards
                                ? [...item.product.tech_cards].sort((a, b) =>
                                      a.product.name.localeCompare(b.product.name)
                                  )
                                : [],
                        },
                    }))
                    .sort((a, b) => a.product.name.localeCompare(b.product.name));

                return {
                    ...newOrder,
                    remainingTime,
                };
            });

            setOrders((prevOrders) => {
                const orderMap = new Map(prevOrders.map((order) => [order.id, order]));

                updatedOrders.forEach((newOrder) => {
                    if (orderMap.has(newOrder.id)) {
                        const existingOrder = orderMap.get(newOrder.id);

                        // Keep the "checked" state for items that haven't changed
                        newOrder.items = newOrder.items.map((newItem) => {
                            const existingItem = existingOrder.items.find(
                                (item) => item.id === newItem.id
                            );
                            return existingItem
                                ? { ...newItem, checked: existingItem.checked }
                                : newItem;
                        });
                    }

                    orderMap.set(newOrder.id, newOrder);
                });

                return Array.from(orderMap.values()).sort(
                    (a, b) => a.number - b.number
                );
            });
        }

        // Handle history response
        if (message.type === 'history' && message.data) {
            setHistoryOrders(message.data.items || []);
            setHistoryPage(message.data.page || 1);
            setHistoryTotalPages(message.data.total_pages || 1);
            setHistoryTotal(message.data.total || 0);
            setHistoryLoading(false);
        }
    }, [parseTimerString]);

    // WebSocket URL - only create when screenId is valid
    const wsUrl = useMemo(() => {
        if (!screenId || screenId === 'undefined' || screenId === 'null') {
            console.warn('Invalid screenId:', screenId);
            return null;
        }
        const apiOrigin = new URL(process.env.REACT_APP_API_URL).origin;
        const wsOrigin = apiOrigin.replace(/^http/, 'ws');
        const url = `${wsOrigin}/ws/${screenId}`;
        console.log('WebSocket URL created:', url);
        return url;
    }, [screenId]);

    // Use WebSocket hook
    const { status, sendMessage } = useWebSocket(wsUrl, {
        onMessage: handleWebSocketMessage,
        onOpen: (event) => {
            console.log(`✅ WebSocket OPENED - Screen: ${screenId}`, event);
        },
        onClose: (event) => {
            console.log(`❌ WebSocket CLOSED - Screen: ${screenId}`, {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                timestamp: new Date().toISOString()
            });
        },
        onError: (error) => {
            console.error("🔥 WebSocket ERROR:", error);
        },
        reconnectInterval: 3000,
        maxReconnectAttempts: 10,
        connectionTimeout: 10000, // 10 seconds - if no connection, retry
    });

    // Request history from WebSocket
    const requestHistory = useCallback((page = 1) => {
        setHistoryLoading(true);
        sendMessage({
            type: 'get_history',
            page: page,
            page_size: 40
        });
    }, [sendMessage]);

    // Open history modal
    const openHistory = useCallback(() => {
        setShowHistory(true);
        setHistoryPage(1);
        requestHistory(1);
    }, [requestHistory]);

    // Close history modal
    const closeHistory = useCallback(() => {
        setShowHistory(false);
        setHistoryOrders([]);
    }, []);

    // Handle history pagination
    const handleHistoryPageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= historyTotalPages) {
            setHistoryPage(newPage);
            requestHistory(newPage);
        }
    }, [historyTotalPages, requestHistory]);

    // Determine the color of the timer text based on remaining time
    const getTimeColor = useCallback((timeInMs) => {
        const timeInMinutes = timeInMs / 60000;
        if (timeInMinutes > 7) return "green";
        if (timeInMinutes > 2) return "#007bff";
        return "red";
    }, []);

    // Returns a textual + emoji label for order_service_type (Russian)
    const getOrderServiceType = useCallback((type) => {
        switch (type) {
            case 0:
                return { text: "В зале", icon: "🍽️" };
            case 1:
                return { text: "Доставка", icon: "🚚" };
            case 2:
                return { text: "Самовывоз", icon: "📦" };
            default:
                return { text: "Неизвестно", icon: "🍽️" };
        }
    }, []);

    // Toggles the "checked" state for a single item on the current screen
    const toggleItemCheck = useCallback((orderId, itemId) => {
        setOrders((prevOrders) =>
            prevOrders.map((order) =>
                order.id === orderId
                    ? {
                          ...order,
                          items: order.items.map((item) =>
                              item.id === itemId && item.current_screen_id === currentScreenId
                                  ? { ...item, checked: !item.checked }
                                  : item
                          ),
                      }
                    : order
            )
        );
    }, [currentScreenId]);

    // Mark all items in an order as "collected" (checked)
    const markCollected = useCallback((orderId) => {
        setOrders((prevOrders) =>
            prevOrders.map((order) =>
                order.id === orderId
                    ? {
                          ...order,
                          items: order.items.map((item) => ({
                              ...item,
                              checked: true,
                          })),
                      }
                    : order
            )
        );
    }, []);

    // Check if all items are checked; if so, we can mark the order as done
    const canMarkDone = useCallback((order) => {
        return order.items.every((item) => item.checked);
    }, []);

    // Mark the order as done in the backend (i.e., move the items off this screen)
    const markDone = useCallback(async (orderId) => {
        const order = orders.find((o) => o.id === orderId);
        if (!order) return;

        const orderItemIdList = order.items
            .filter((item) => item.checked)
            .map((item) => item.id);

        try {
            await updateOrderItems(order.id, currentScreenId, orderItemIdList);
            setOrders((prevOrders) => prevOrders.filter((o) => o.id !== orderId));
        } catch (error) {
            console.error("Error updating order items:", error);
        }
    }, [orders, currentScreenId]);

    // Utility to format the order ID to "xxxxx-xxxxx" form
    const formatOrderId = useCallback((uuid) => {
        const firstPart = uuid.slice(0, 5);
        const lastPart = uuid.slice(-5);
        return `${firstPart}-${lastPart}`;
    }, []);

    // Get status display (Russian)
    const getStatusDisplay = useCallback(() => {
        switch (status) {
            case 'connected':
                return { text: '🟢 Подключено', color: '#22c55e' };
            case 'connecting':
                return { text: '🟡 Подключение...', color: '#eab308' };
            case 'reconnecting':
                return { text: '🟠 Переподключение...', color: '#f97316' };
            case 'disconnected':
                return { text: '🔴 Отключено', color: '#ef4444' };
            case 'error':
                return { text: '❌ Ошибка соединения', color: '#dc2626' };
            default:
                return { text: '⚪ Неизвестно', color: '#9ca3af' };
        }
    }, [status]);

    const statusDisplay = getStatusDisplay();

    // Don't render if screenId is not available
    if (!screenId || screenId === 'undefined' || screenId === 'null') {
        return (
            <div className="screen-container">
                <h1 className="screen-title">Ошибка: Неверный ID экрана</h1>
                <p>Пожалуйста, проверьте URL и попробуйте снова.</p>
            </div>
        );
    }

    return (
        <div className="screen-container">
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <h1 className="screen-title" style={{ margin: 0 }}>{currentScreenName}</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button
                        onClick={openHistory}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            padding: '0.375rem 0.75rem',
                            fontSize: '0.8125rem',
                            fontWeight: '600',
                            color: '#6366f1',
                            background: '#eef2ff',
                            border: '1px solid #c7d2fe',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            fontFamily: 'inherit'
                        }}
                    >
                        📋 История
                    </button>
                    <div style={{ 
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: statusDisplay.color,
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0.375rem',
                        backgroundColor: `${statusDisplay.color}15`,
                        border: `1px solid ${statusDisplay.color}40`
                    }}>
                        {statusDisplay.text}
                    </div>
                </div>
            </div>
            <main className="orders-container">
                {orders.map((order) => {
                    const { text, icon } = getOrderServiceType(order.order_service_type);

                    return (
                        <div
                            className="order-card"
                            key={order.id}
                            style={{
                                borderColor: getTimeColor(order.remainingTime),
                            }}
                        >
                            <header className="order-header">
                                <div className="order-header-row">
                                    <span className="order-id">№ {order.external_order_number || formatOrderId(order.id)}</span>
                                    <span className="order-number">{order.number}</span>
                                </div>
                                <div className="order-header-row">
                                    <span title={text} className="flex order-service-type">
                                        <span className="emoji-icon">{icon}</span>
                                        <span className="origin-name">{order.origin_name}</span>
                                    </span>
                                    <span
                                        className="order-timer"
                                        style={{
                                            color: getTimeColor(order.remainingTime),
                                        }}
                                    >
                                        {formatTime(order.remainingTime)}
                                    </span>
                                </div>
                            </header>
                            <ul className="order-items">
                                {order.items.map((item) => (
                                    <li className="order-item" key={item.id}>
                                        <div className="item-name">
                                            <span>{item.product.name}</span>
                                            <span className="item-amount">x{item.amount}</span>
                                            <input
                                                type="checkbox"
                                                checked={item.checked}
                                                disabled={item.current_screen_id !== currentScreenId}
                                                onChange={() =>
                                                    toggleItemCheck(order.id, item.id)
                                                }
                                            />
                                        </div>
                                        {item.product.tech_cards?.length > 0 && (
                                            <ul className="tech-cards">
                                                {item.product.tech_cards.map((techCard) => (
                                                    <li
                                                        className="tech-card-item"
                                                        key={techCard.id}
                                                    >
                                                        <span className="tech-card-name">
                                                            {techCard.product.name}
                                                        </span>
                                                        <span className="tech-card-amount">
                                                            x{techCard.amount}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <footer className="order-footer">
                                <button
                                    className="btn done"
                                    onClick={() => markDone(order.id)}
                                    disabled= {!canMarkDone(order)}
                                >
                                    Готово
                                </button>
                                <button
                                    className="btn collected"
                                    onClick={() => markCollected(order.id)}
                                    disabled={order.items.some(
                                        (item) => item.current_screen_id !== currentScreenId
                                    )}
                                >
                                    Собрано
                                </button>
                            </footer>
                        </div>
                    );
                })}
            </main>

            {/* History Modal */}
            {showHistory && (
                <div 
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={closeHistory}
                >
                    <div 
                        style={{
                            background: 'white',
                            borderRadius: '1rem',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                            maxWidth: '800px',
                            width: '95%',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem 1.25rem',
                            borderBottom: '1px solid #e2e8f0',
                            background: '#f8fafc'
                        }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: '#1e293b' }}>
                                    📋 История заказов
                                </h2>
                                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                                    Закрытые заказы за сегодня • Всего: {historyTotal}
                                </p>
                            </div>
                            <button
                                onClick={closeHistory}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#64748b',
                                    lineHeight: 1
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{
                            flex: 1,
                            overflow: 'auto',
                            padding: '1rem'
                        }}>
                            {historyLoading ? (
                                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '3px solid #e2e8f0',
                                        borderTopColor: '#3b82f6',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite',
                                        margin: '0 auto 1rem'
                                    }} />
                                    Загрузка...
                                </div>
                            ) : historyOrders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.5 }}>📭</div>
                                    <p style={{ margin: 0 }}>Нет закрытых заказов за сегодня</p>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '0.75rem'
                                }}>
                                    {historyOrders.map((order) => {
                                        const closedTime = order.updated_at ? new Date(order.updated_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
                                        const price = order.result_sum ? Math.round(parseFloat(order.result_sum)).toLocaleString('ru-RU') : '0';
                                        
                                        return (
                                            <div
                                                key={order.id}
                                                style={{
                                                    background: 'white',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '0.75rem',
                                                    padding: '0.875rem',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {/* Header Row */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '0.625rem',
                                                    paddingBottom: '0.625rem',
                                                    borderBottom: '1px solid #f1f5f9'
                                                }}>
                                                    <span style={{
                                                        fontWeight: '800',
                                                        fontSize: '0.8125rem',
                                                        color: '#1e293b',
                                                        background: '#e0e7ff',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '0.375rem',
                                                        fontFamily: "'SF Mono', 'Monaco', monospace"
                                                    }}>
                                                        № {order.external_order_number || formatOrderId(order.id)}
                                                    </span>
                                                    <span style={{
                                                        fontWeight: '700',
                                                        fontSize: '1rem',
                                                        color: '#6366f1'
                                                    }}>
                                                        #{order.number}
                                                    </span>
                                                </div>

                                                {/* Origin & Time Row */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    <span style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.375rem',
                                                        fontSize: '0.75rem',
                                                        color: '#475569'
                                                    }}>
                                                        {getOrderServiceType(order.order_service_type).icon} {order.origin_name}
                                                    </span>
                                                    <span style={{
                                                        fontSize: '0.6875rem',
                                                        color: '#94a3b8',
                                                        background: '#f8fafc',
                                                        padding: '0.125rem 0.375rem',
                                                        borderRadius: '0.25rem'
                                                    }}>
                                                        🕐 {closedTime}
                                                    </span>
                                                </div>

                                                {/* Price Row */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    background: '#f0fdf4',
                                                    padding: '0.5rem 0.625rem',
                                                    borderRadius: '0.5rem',
                                                    marginTop: '0.375rem'
                                                }}>
                                                    <span style={{
                                                        fontSize: '0.6875rem',
                                                        color: '#16a34a',
                                                        fontWeight: '500'
                                                    }}>
                                                        💰 Сумма
                                                    </span>
                                                    <span style={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: '700',
                                                        color: '#15803d'
                                                    }}>
                                                        {price} сум
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer - Pagination */}
                        {historyTotalPages > 1 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem',
                                borderTop: '1px solid #e2e8f0',
                                background: '#f8fafc'
                            }}>
                                <button
                                    onClick={() => handleHistoryPageChange(historyPage - 1)}
                                    disabled={historyPage <= 1}
                                    style={{
                                        padding: '0.375rem 0.75rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        color: historyPage <= 1 ? '#94a3b8' : '#475569',
                                        background: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '0.375rem',
                                        cursor: historyPage <= 1 ? 'not-allowed' : 'pointer',
                                        fontFamily: 'inherit'
                                    }}
                                >
                                    ← Назад
                                </button>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', padding: '0 0.5rem' }}>
                                    Стр. {historyPage} из {historyTotalPages}
                                </span>
                                <button
                                    onClick={() => handleHistoryPageChange(historyPage + 1)}
                                    disabled={historyPage >= historyTotalPages}
                                    style={{
                                        padding: '0.375rem 0.75rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        color: historyPage >= historyTotalPages ? '#94a3b8' : '#475569',
                                        background: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '0.375rem',
                                        cursor: historyPage >= historyTotalPages ? 'not-allowed' : 'pointer',
                                        fontFamily: 'inherit'
                                    }}
                                >
                                    Далее →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScreensPage;
